import { useState } from 'react';
import api from '@/lib/api';
import { Link as LinkIcon, Copy, RefreshCw, X, Eye, Check, AlertTriangle, Send, Mail, MessageCircle, Smartphone, Monitor, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { timeAgo, HOT_VIEWS } from '@/lib/tracking';

function SendButtons({ quote, shareUrl, onSent }) {
  const name = quote.customer_name ? ` ${quote.customer_name.split(' ')[0]}` : '';
  const msg = `Hi${name}, here's your quote${quote.quote_number ? ` (${quote.quote_number})` : ''}: ${shareUrl}`;
  const phone = (quote.customer_phone || '').replace(/[^\d+]/g, '').replace(/^0/, '44');
  const links = [
    { id: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle, href: `https://wa.me/${phone.replace('+', '')}?text=${encodeURIComponent(msg)}` },
    { id: 'sms', label: 'Text', Icon: Smartphone, href: `sms:${quote.customer_phone || ''}?&body=${encodeURIComponent(msg)}` },
    { id: 'email', label: 'Email', Icon: Mail, href: `mailto:${quote.customer_email || ''}?subject=${encodeURIComponent(`Your quote${quote.quote_number ? ` ${quote.quote_number}` : ''}`)}&body=${encodeURIComponent(msg)}` },
  ];
  const nativeShare = async () => {
    try { await navigator.share({ title: 'Your quote', text: msg, url: shareUrl }); onSent(); } catch { /* cancelled */ }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="share-send-buttons">
      {links.map(({ id, label, Icon, href }) => (
        <a key={id} href={href} target="_blank" rel="noreferrer" onClick={onSent} data-testid={`share-send-${id}`}
           className="tm-btn tm-btn-ghost text-xs min-h-0 py-2"><Icon size={14} /> {label}</a>
      ))}
      {typeof navigator !== 'undefined' && navigator.share && (
        <button type="button" onClick={nativeShare} data-testid="share-send-native" className="tm-btn tm-btn-ghost text-xs min-h-0 py-2">
          <Send size={14} /> More…
        </button>
      )}
    </div>
  );
}

const DEVICE = { desktop: ['Computer', Monitor], tablet: ['Tablet', Smartphone], mobile: ['Phone', Smartphone] };
const fmtExact = (iso) => new Date(iso).toLocaleString('en-GB', {
  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

function ViewHistory({ log, token }) {
  const [all, setAll] = useState(false);
  if (!log?.length) return null;
  const rows = [...log].reverse();
  const visits = log.filter((v) => v.new_visit !== false).length;
  const shown = all ? rows : rows.slice(0, 10);
  return (
    <div data-testid="share-view-history">
      <p className="tm-label mt-4">Every time the customer opened it</p>
      <p className="text-xs text-slate-500 mb-2" data-testid="share-view-summary">
        Opened {log.length} time{log.length === 1 ? '' : 's'} · {visits} separate visit{visits === 1 ? '' : 's'} · first opened {fmtExact(log[0].at)}
      </p>
      <ol className="space-y-1 max-h-80 overflow-y-auto pr-1">
        {shown.map((v, i) => {
          const [label, Icon] = DEVICE[v.device] || DEVICE.mobile;
          return (
            <li key={`${v.at}-${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-600" data-testid={`share-view-row-${i}`}>
              <Icon size={13} className="text-slate-400" />
              <span className="font-semibold text-slate-800">#{log.length - i}</span>
              <span className="tabular-nums">{fmtExact(v.at)}</span>
              <span className="text-slate-400">· {label}</span>
              {v.new_visit === false && <span className="text-slate-400 italic">· re-opened within 10 min</span>}
              {(!token || (v.link && v.link !== token.slice(-6))) && <span className="text-amber-600 italic">· earlier link</span>}
            </li>
          );
        })}
      </ol>
      {rows.length > 10 && (
        <button type="button" onClick={() => setAll(!all)} data-testid="share-view-toggle-all" className="mt-2 text-xs font-semibold text-brand hover:underline">
          {all ? 'Show fewer' : `Show all ${rows.length} opens`}
        </button>
      )}
    </div>
  );
}

function Activity({ quote }) {
  const viewed = !!quote.share_first_viewed_at;
  const decided = !!quote.share_decision_at;
  const hot = !decided && (quote.share_views || 0) >= HOT_VIEWS;
  return (
    <div className="border border-slate-200 bg-slate-50 rounded-xl p-4 text-sm" data-testid="share-activity">
      <div className="flex items-center justify-between mb-2">
        <p className="tm-label mb-0">Activity</p>
        {hot && <span className="tm-chip tm-chip-orange" data-testid="share-hot-badge"><Flame size={12} /> Hot lead</span>}
      </div>
      <ul className="space-y-1">
        <li className="flex items-center gap-2">
          {quote.share_sent_at ? <Check size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-amber-500" />}
          <span data-testid="share-sent-state">{quote.share_sent_at ? `Link sent ${timeAgo(quote.share_sent_at)}` : 'Link not sent yet — copy or send it below'}</span>
        </li>
        <li className="flex items-center gap-2">
          {viewed ? <Check size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-amber-500" />}
          <span data-testid="share-view-state">
            {viewed
              ? `Viewed ${quote.share_views || 1}× · last ${timeAgo(quote.share_last_viewed_at || quote.share_first_viewed_at)}`
              : 'Not yet viewed by the customer'}
          </span>
        </li>
        {decided ? (
          <li className="flex items-center gap-2">
            <Check size={14} className={quote.status === 'accepted' ? 'text-emerald-600' : 'text-rose-600'} />
            <span>Customer <span className="font-bold">{quote.status}</span> on {new Date(quote.share_decision_at).toLocaleString('en-GB')}{quote.share_decision_signature ? ` · signed: ${quote.share_decision_signature}` : ''}</span>
          </li>
        ) : viewed && (
          <li className="flex items-center gap-2 text-amber-700">
            <AlertTriangle size={14} />
            <span>{hot ? 'Opened several times — a good moment to give them a call' : 'Awaiting customer decision'}</span>
          </li>
        )}
        {decided && quote.share_decision_note && (
          <li className="text-slate-600 ml-5">Note from customer: &ldquo;{quote.share_decision_note}&rdquo;</li>
        )}
      </ul>
      <ViewHistory log={quote.share_view_log} token={quote.share_token} />
      <p className="text-[11px] text-slate-400 mt-3">You get an email each time they come back to the quote (at most once every 30 minutes). Refreshes within 10 minutes count as one view.</p>
    </div>
  );
}

export default function ShareSection({ projectId, quote, onUpdate }) {
  const [busy, setBusy] = useState(false);
  if (!quote) return null;
  const shareUrl = quote.share_token ? `${window.location.origin}/q/${quote.share_token}` : null;

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/share`);
      onUpdate?.(data);
      toast.success('Share link ready');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create link');
    } finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm('Revoke this link? The customer will no longer be able to view this quote.')) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/projects/${projectId}/share`);
      onUpdate?.(data);
      toast.success('Link revoked');
    } catch { toast.error('Revoke failed'); }
    finally { setBusy(false); }
  };
  const markSent = async () => {
    if (quote.share_sent_at) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/share/sent`);
      onUpdate?.(data);
    } catch { /* non-critical */ }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied — quote filed under Sent');
    markSent();
  };

  if (!projectId) {
    return (
      <div className="tm-card p-6 bg-slate-50">
        <div className="flex items-center gap-2 mb-2">
          <LinkIcon size={18} className="text-brand" />
          <h3 className="font-display font-bold text-lg tracking-tight">Customer share link</h3>
        </div>
        <p className="text-sm text-slate-600">Save this quote first, then you can generate a link to send to the customer.</p>
      </div>
    );
  }

  return (
    <div className="tm-card p-6 space-y-4">
      <div className="flex items-center gap-2">
        <LinkIcon size={18} className="text-brand" />
        <h3 className="font-display font-bold text-xl tracking-tight">Customer share link</h3>
      </div>
      <p className="text-sm text-slate-600">Send this link to your customer. They&apos;ll see a clean branded quote and can accept or decline directly — no login required.</p>

      {shareUrl ? (
        <>
          <div className="flex flex-wrap gap-2">
            <input data-testid="share-link" readOnly value={shareUrl} className="tm-input flex-1 font-mono text-sm" onFocus={(e) => e.target.select()} />
            <button data-testid="share-copy" onClick={copy} className="tm-btn"><Copy size={16} /> Copy</button>
            <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="share-preview" className="tm-btn tm-btn-ghost">
              <Eye size={16} /> Preview
            </a>
          </div>
          <SendButtons quote={quote} shareUrl={shareUrl} onSent={markSent} />
          <Activity quote={quote} />
          <button data-testid="share-revoke" onClick={revoke} disabled={busy} className="tm-btn tm-btn-ghost text-xs min-h-0 py-2">
            <X size={14} /> Revoke &amp; create new link
          </button>
        </>
      ) : (
        <>
          {quote.share_view_log?.length > 0 && (
            <div className="border border-slate-200 bg-slate-50 rounded-xl p-4" data-testid="share-past-history">
              <p className="text-xs text-slate-500">History from previous links</p>
              <ViewHistory log={quote.share_view_log} />
            </div>
          )}
          <button data-testid="share-create" onClick={create} disabled={busy} className="tm-btn">
            <RefreshCw size={16} /> {busy ? 'Creating…' : 'Generate share link'}
          </button>
        </>
      )}
    </div>
  );
}
